'use strict';

/**
 * Centralized compensation gap computation.
 *
 * Sign convention (canonical across backend + UI):
 *   gapAmount = market - pay   (positive = below market, negative = above market)
 *   gapPct    = (market - pay) / market * 100  (same sign as gapAmount)
 */

function finiteNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function roundPct(v) {
  const n = finiteNumber(v);
  if (n == null) return null;
  return Math.round(n * 10) / 10;
}

function roundMoney(v) {
  const n = finiteNumber(v);
  if (n == null) return null;
  return Math.round(n);
}

/**
 * @param {number|null|undefined} market
 * @param {number|null|undefined} pay
 * @returns {number|null}
 */
function computeCompGapAmount(market, pay) {
  const m = finiteNumber(market);
  const p = finiteNumber(pay);
  if (m == null || p == null) return null;
  return roundMoney(m - p);
}

/**
 * @param {number|null|undefined} market
 * @param {number|null|undefined} pay
 * @returns {number|null}
 */
function computeCompGapPct(market, pay) {
  const m = finiteNumber(market);
  const p = finiteNumber(pay);
  if (m == null || p == null || m <= 0) return null;
  return roundPct(((m - p) / m) * 100);
}

function signDirection(n, epsilon = 0.05) {
  const v = finiteNumber(n);
  if (v == null) return 0;
  if (v > epsilon) return 1;
  if (v < -epsilon) return -1;
  return 0;
}

/**
 * @param {{ market?: number|null, pay?: number|null, gapAmount?: number|null, gapPct?: number|null, basis?: string }} input
 * @returns {{
 *   gapAmount: number|null,
 *   gapPct: number|null,
 *   direction: 'below'|'above'|'at'|null,
 *   basis: string|null,
 * }}
 */
function derivePayGapMetrics(input = {}) {
  const market = finiteNumber(input.market);
  const pay = finiteNumber(input.pay);
  const basis = input.basis || null;

  const gapAmount =
    market != null && pay != null ? computeCompGapAmount(market, pay) : roundMoney(input.gapAmount);
  const gapPct =
    market != null && pay != null && market > 0
      ? computeCompGapPct(market, pay)
      : roundPct(input.gapPct);

  let direction = null;
  const dirFromAmount = signDirection(gapAmount);
  const dirFromPct = signDirection(gapPct);
  const resolvedDir = dirFromAmount !== 0 ? dirFromAmount : dirFromPct;
  if (resolvedDir > 0) direction = 'below';
  else if (resolvedDir < 0) direction = 'above';
  else if (gapAmount === 0 || gapPct === 0) direction = 'at';

  return { gapAmount, gapPct, direction, basis };
}

module.exports = {
  finiteNumber,
  roundPct,
  roundMoney,
  computeCompGapAmount,
  computeCompGapPct,
  derivePayGapMetrics,
  signDirection,
};
